import { useQuest } from "@/context/QuestProvider";
import { UPDATE_QUEST_GROUP } from "@/context/QuestProvider/actions";
import type { LatestEarnings } from "@/domain/gems-game/domain";
import type { Content } from "@prismicio/client";
import type { Quest, QuestGroup, QuestState } from "@ui-kit/QuestList";
import { useCallback, useEffect, useRef } from "react";
import { useOrderingQuest } from "../useOrderingQuest";
import { computeQuestStatusFields } from "./transform/computeQuestStatusFields";
import { reorderQuestsBySkipOrder } from "./transform/reorderQuestsBySkipOrder";

export const useInitialiseQuestGroup = (
  walletAddress: string | undefined,
  game: Content.GameDocument,
  questGroups: QuestGroup[],
  latestEarnings: LatestEarnings | undefined,
) => {
  const { dispatchAction } = useQuest((store) => store);
  // Retrieve the quest order from local storage or create a new one if it doesn't exist
  const questOrder = useOrderingQuest(walletAddress, game, questGroups);

  // Ref to ensure auto-initialisation runs only once on mount.
  const hasRetrievedRef = useRef(false);

  // Enrich quests with additional fields.
  const createQuestGroupWithQuestStatus = useCallback(
    (questGroups: QuestGroup[]) => {
      return questGroups.map((currentGroup, groupIndex) => ({
        ...currentGroup,
        quests: currentGroup.quests.map(
          (currentQuest: Quest, questIndex: number) => {
            const timeAndStatus = computeQuestStatusFields(
              questGroups,
              currentGroup,
              groupIndex,
              currentQuest,
              questIndex,
              latestEarnings,
            );
            return { ...currentQuest, ...timeAndStatus } as QuestState;
          },
        ),
      }));
    },
    [latestEarnings],
  );

  const initialiseQuestGroups = useCallback(() => {
    // If there are no skippable quests bypass the reordering so order updates are reflected
    let hasSkippableQuest = false;
    for (const questGroup of questGroups) {
      for (const quest of questGroup.quests) {
        if (quest.skippable) {
          hasSkippableQuest = true;
          break;
        }
      }
      if (hasSkippableQuest) {
        break;
      }
    }

    // Merge quest order from local storage with hardcoded quest groups data
    const questGroupWithOrderedQuest = hasSkippableQuest
      ? reorderQuestsBySkipOrder(questGroups, questOrder)
      : questGroups;

    // Enrich the quest groups by adding status and lock fields.
    const questGroupWithStatusTime = createQuestGroupWithQuestStatus(
      questGroupWithOrderedQuest,
    );

    // Dispatch the enriched quest state to the QuestProvider store.
    dispatchAction({
      type: UPDATE_QUEST_GROUP,
      payload: {
        id: "initialise-quests",
        questGroups: questGroupWithStatusTime,
      },
    });
  }, [
    questGroups,
    questOrder,
    dispatchAction,
    createQuestGroupWithQuestStatus,
  ]);

  // Auto-initialisation effect, using hasRetrievedRef to run only once on mount.
  useEffect(() => {
    if (!hasRetrievedRef.current) {
      initialiseQuestGroups();
      hasRetrievedRef.current = true;
    }
  }, [initialiseQuestGroups]);

  // Return a function that manually triggers Quest Group initialisation.
  return { reInitialiseQuestGroup: initialiseQuestGroups };
};
